import { AABB, RaycastHit } from '../types';
import { PLAYER_WIDTH, PLAYER_HEIGHT, GRAVITY, BLOCK_REACH } from '../constants';
import { Face, FACE_NORMALS } from '../utils/direction';

export interface PhysicsBody {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  width: number;
  height: number;
  onGround: boolean;
}

export function createPlayerBody(x: number, y: number, z: number): PhysicsBody {
  return {
    x, y, z,
    vx: 0, vy: 0, vz: 0,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    onGround: false,
  };
}

export function getAABB(body: PhysicsBody): AABB {
  const hw = body.width / 2;
  return {
    minX: body.x - hw,
    minY: body.y,
    minZ: body.z - hw,
    maxX: body.x + hw,
    maxY: body.y + body.height,
    maxZ: body.z + hw,
  };
}

export function aabbOverlap(a: AABB, b: AABB): boolean {
  return (
    a.minX < b.maxX && a.maxX > b.minX &&
    a.minY < b.maxY && a.maxY > b.minY &&
    a.minZ < b.maxZ && a.maxZ > b.minZ
  );
}

export function blockAABB(bx: number, by: number, bz: number): AABB {
  return {
    minX: bx, minY: by, minZ: bz,
    maxX: bx + 1, maxY: by + 1, maxZ: bz + 1,
  };
}

export function updatePhysics(
  body: PhysicsBody,
  dt: number,
  isSolid: (x: number, y: number, z: number) => boolean,
): void {
  body.vy += GRAVITY * dt;

  // Resolve Y first (gravity), then X, then Z
  moveAxis(body, 0, body.vy * dt, 0, isSolid);
  moveAxis(body, body.vx * dt, 0, 0, isSolid);
  moveAxis(body, 0, 0, body.vz * dt, isSolid);
}

function moveAxis(
  body: PhysicsBody,
  dx: number,
  dy: number,
  dz: number,
  isSolid: (x: number, y: number, z: number) => boolean,
): void {
  body.x += dx;
  body.y += dy;
  body.z += dz;

  const hw = body.width / 2;
  const aabb = getAABB(body);

  const minBX = Math.floor(aabb.minX);
  const maxBX = Math.floor(aabb.maxX);
  const minBY = Math.floor(aabb.minY);
  const maxBY = Math.floor(aabb.maxY);
  const minBZ = Math.floor(aabb.minZ);
  const maxBZ = Math.floor(aabb.maxZ);

  for (let bx = minBX; bx <= maxBX; bx++) {
    for (let by = minBY; by <= maxBY; by++) {
      for (let bz = minBZ; bz <= maxBZ; bz++) {
        if (!isSolid(bx, by, bz)) continue;

        const block = blockAABB(bx, by, bz);
        const playerAABB = getAABB(body);

        if (!aabbOverlap(playerAABB, block)) continue;

        if (dy < 0) {
          body.y = block.maxY;
          body.vy = 0;
          body.onGround = true;
        } else if (dy > 0) {
          body.y = block.minY - body.height;
          body.vy = 0;
        }

        if (dx < 0) {
          body.x = block.maxX + hw;
          body.vx = 0;
        } else if (dx > 0) {
          body.x = block.minX - hw;
          body.vx = 0;
        }

        if (dz < 0) {
          body.z = block.maxZ + hw;
          body.vz = 0;
        } else if (dz > 0) {
          body.z = block.minZ - hw;
          body.vz = 0;
        }
      }
    }
  }

  // If we moved in Y and didn't hit ground, not on ground
  if (dy !== 0) {
    const feet = getAABB(body);
    let grounded = false;
    for (let bx = Math.floor(feet.minX); bx <= Math.floor(feet.maxX); bx++) {
      for (let bz = Math.floor(feet.minZ); bz <= Math.floor(feet.maxZ); bz++) {
        if (isSolid(bx, Math.floor(body.y - 0.01), bz)) {
          grounded = true;
        }
      }
    }
    if (!grounded && dy < 0) {
      body.onGround = false;
    }
  }
}

export function raycast(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDist: number,
  isSolid: (x: number, y: number, z: number) => boolean,
): RaycastHit | null {
  let x = Math.floor(ox);
  let y = Math.floor(oy);
  let z = Math.floor(oz);

  const stepX = dx > 0 ? 1 : -1;
  const stepY = dy > 0 ? 1 : -1;
  const stepZ = dz > 0 ? 1 : -1;

  const tDeltaX = dx === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = dy === 0 ? Infinity : Math.abs(1 / dy);
  const tDeltaZ = dz === 0 ? Infinity : Math.abs(1 / dz);

  let tMaxX = dx === 0 ? Infinity : ((dx > 0 ? x + 1 - ox : ox - x) / Math.abs(dx));
  let tMaxY = dy === 0 ? Infinity : ((dy > 0 ? y + 1 - oy : oy - y) / Math.abs(dy));
  let tMaxZ = dz === 0 ? Infinity : ((dz > 0 ? z + 1 - oz : oz - z) / Math.abs(dz));

  let face = Face.TOP;
  let dist = 0;

  while (dist < maxDist) {
    if (isSolid(x, y, z)) {
      const normal = FACE_NORMALS[face] as [number, number, number];
      return { x, y, z, face, normal, distance: dist };
    }

    if (tMaxX < tMaxY) {
      if (tMaxX < tMaxZ) {
        dist = tMaxX;
        x += stepX;
        tMaxX += tDeltaX;
        face = stepX > 0 ? Face.LEFT : Face.RIGHT;
      } else {
        dist = tMaxZ;
        z += stepZ;
        tMaxZ += tDeltaZ;
        face = stepZ > 0 ? Face.BACK : Face.FRONT;
      }
    } else {
      if (tMaxY < tMaxZ) {
        dist = tMaxY;
        y += stepY;
        tMaxY += tDeltaY;
        face = stepY > 0 ? Face.BOTTOM : Face.TOP;
      } else {
        dist = tMaxZ;
        z += stepZ;
        tMaxZ += tDeltaZ;
        face = stepZ > 0 ? Face.BACK : Face.FRONT;
      }
    }
  }

  return null;
}
